import React, { useEffect } from 'react';

type ScopusHOCProps = {
  children: React.ReactNode;
  withExperimentalScript?: boolean;
  withComponentsScript?: boolean;
  withComponentsStylesheet?: boolean;
};

const DEFAULT_SCOPUS_STYLESHEET = 'https://components.scopus.com/www/stylesheet/scopus-stylesheet.css';
const CUSTOM_SCOPUS_STYLESHEET_PROD = 'https://components.scopus.com/www/css/scopus-styles.css';

const addScript = ({ mode, src }: { mode?: string; src: string }): void => {
  const script = document.createElement('script');
  script.src = src;
  if (mode === 'nomodule') {
    script.setAttribute('nomodule', '');
  } else if (mode === 'module') {
    script.type = 'module';
  }
  document.head.appendChild(script);
};

const ScopusHOC = React.memo(
  ({
    children,
    withExperimentalScript = true,
    withComponentsScript = true,
    withComponentsStylesheet = true,
  }: ScopusHOCProps): JSX.Element => {
    useEffect(() => {
      const hasScopusStylesheet =
        !!document.querySelector('link[href$="scopus-stylesheet.css"]') ||
        !!document.querySelector('link[href$="scopus-styles.css"]');

      if (!hasScopusStylesheet && withComponentsStylesheet) {
        let stylesheetUrl = DEFAULT_SCOPUS_STYLESHEET;
        if (window.location.host.includes('scopus.com')) {
          stylesheetUrl = CUSTOM_SCOPUS_STYLESHEET_PROD;
        }
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.type = 'text/css';
        stylesheet.href = stylesheetUrl;
        document.head.appendChild(stylesheet);
      }

      const hasComponentsScript = document.querySelector('script[src$="scopus-components.js"]');
      if (!hasComponentsScript && withComponentsScript) {
        const scopusConfiguration = document.createElement('script');
        scopusConfiguration.innerHTML = `
          const getConfigurationValue = window.scopus?.platform?.configuration?.getValue;
          window.scopus = {
            platform: {
              ...(window.scopus?.platform || {}),
              configuration: {
                ...(window.scopus?.platform?.configuration || {}),
                getValue: (key) => getConfigurationValue?.(key) || (key === 'STYLEGUIDE_BUNDLE_BASE_URL' ? 'https://components.scopus.com/www' : undefined)
              }
            }
          };
        `;
        document.head.appendChild(scopusConfiguration);

        addScript({ src: 'https://components.scopus.com/www/components/scopus-components.js' });
      }

      const hasExperimentalScript = document.querySelector(
        'script[src$="experimental.esm.js"],script[src$="experimental.js"]',
      );

      if (!hasExperimentalScript && withExperimentalScript) {
        addScript({
          mode: 'module',
          src: 'https://components.scopus.com/www/experimental-components/experimental/experimental.esm.js',
        });
        addScript({
          mode: 'nomodule',
          src: 'https://components.scopus.com/www/experimental-components/experimental/experimental.js',
        });
      }
    }, [withComponentsScript, withExperimentalScript, withComponentsStylesheet]);

    return <div className="micro-ui-namespace">{children}</div>;
  }
);

ScopusHOC.displayName = 'ScopusHOC';

export default ScopusHOC;
